// Shopify Configuration for Berleto
// This file handles the integration with Shopify Storefront API
// Only prices and checkout functionality come from Shopify

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN || 'berleto.com';
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN || '';
const SHOPIFY_API_VERSION = '2023-10'; // Use stable API version

// Product Handle Mapping - CORRECTED with actual Shopify handles
// Maps our local product IDs to Shopify product handles
export const PRODUCT_HANDLE_MAP = {
  'rush': 'rush',
  'synaptiq': 'synaptiq',
  'vitra': 'vitra',
  'bundle': 'complete-wellness-bundle'
};

// Create Storefront API URL - use primary domain for custom domain stores
export const getStorefrontApiUrl = () => {
  return `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
};

// Storefront API Headers - try multiple header formats
export const getStorefrontHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  // Try the standard Storefront API header
  if (SHOPIFY_STOREFRONT_ACCESS_TOKEN) {
    headers['X-Shopify-Storefront-Access-Token'] = SHOPIFY_STOREFRONT_ACCESS_TOKEN;
  }
  
  return headers;
};

// Simple shop info query for testing - CORRECTED for Storefront API
export const SHOP_INFO_QUERY = `
  query {
    shop {
      name
      paymentSettings {
        currencyCode
      }
      primaryDomain {
        host
      }
    }
  }
`;

// Simple products query for testing
export const PRODUCTS_QUERY = `
  query {
    products(first: 10) {
      edges {
        node {
          handle
          title
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

// GraphQL Queries
export const PRODUCT_PRICE_QUERY = `
  query getProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      handle
      title
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 1) {
        edges {
          node {
            id
            availableForSale
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

// Alternative query without variables (working version from tests)
export const PRODUCT_PRICE_QUERY_SIMPLE = (handle) => `
  query {
    productByHandle(handle: "${handle}") {
      id
      handle
      title
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 1) {
        edges {
          node {
            id
            availableForSale
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;



// Test connection to Shopify first
export async function testShopifyConnection() {
  console.log('🔍 Testing Shopify connection...');
  
  try {
    const url = getStorefrontApiUrl();
    const headers = getStorefrontHeaders();
    
    console.log('Testing with URL:', url);
    console.log('Testing with headers:', headers);
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query: SHOP_INFO_QUERY })
    });
    
    console.log('Shop test response status:', response.status);
    
    if (!response.ok) {
      const text = await response.text();
      console.error('Shop test error response:', text);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Shop test response:', data);
    
    if (data.errors) {
      console.error('GraphQL errors in shop test:', data.errors);
      return { success: false, errors: data.errors };
    }
    
    if (data.data?.shop) {
      console.log('✅ Shopify connection successful:', data.data.shop);
      return { success: true, shop: data.data.shop };
    }
    
    return { success: false, error: 'No shop data returned' };
    
  } catch (error) {
    console.error('❌ Shopify connection test failed:', error);
    return { success: false, error: error.message };
  }
}

// Fetch product price from Shopify
export async function fetchProductPrice(productId) {
  const handle = PRODUCT_HANDLE_MAP[productId];
  if (!handle) {
    console.error(`No Shopify handle found for product: ${productId}`);
    return null;
  }

  console.log(`🔍 Fetching price for product: ${productId} (handle: ${handle})`);
  
  // Debug environment variables
  console.log('🔧 Debug Environment:', {
    domain: process.env.REACT_APP_SHOPIFY_DOMAIN,
    tokenLength: process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN?.length,
    tokenStart: process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN?.substring(0, 8) + '...'
  });

  try {
    const url = getStorefrontApiUrl();
    const headers = getStorefrontHeaders();
    
    console.log('Product API Request:', { url, headers, handle });

    console.log('🔍 Sending query:', PRODUCT_PRICE_QUERY_SIMPLE(handle));

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: PRODUCT_PRICE_QUERY_SIMPLE(handle)
      })
    });

    console.log('Product Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product HTTP Error Response:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Product API Response data:', data);
    
    if (data.errors) {
      console.error('Product GraphQL errors:', data.errors);
      return null;
    }
    
    if (data.data?.productByHandle) {
      const product = data.data.productByHandle;
      const price = product.priceRange.minVariantPrice.amount;
      const variantId = product.variants.edges[0]?.node.id;
      
      const result = {
        price: parseFloat(price),
        currencyCode: product.priceRange.minVariantPrice.currencyCode,
        variantId,
        available: product.variants.edges[0]?.node.availableForSale ?? true
      };
      
      console.log('✅ Product price fetch successful:', result);
      return result;
    }
    
    console.log('❌ No product found in response for handle:', handle);
    return null;
  } catch (error) {
    console.error('❌ Error fetching product price:', error);
    return null;
  }
}

// Updated Cart Mutations for Shopify Storefront API 2023-10
export const CREATE_CART_MUTATION = `
  mutation createCart($lineItems: [CartLineInput!], $attributes: [AttributeInput!]) {
    cartCreate(input: { lines: $lineItems, attributes: $attributes }) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
                priceAdjustments {
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  perDeliveryPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const ADD_TO_CART_MUTATION = `
  mutation addToCart($cartId: ID!, $lineItems: [CartLineInput!]) {
    cartLinesAdd(cartId: $cartId, lines: $lineItems) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Update createCheckout to use Cart API.
// `attributes` (e.g. Meta _fbp/_fbc/_fb_event_id) are stored on the cart and
// arrive on the order as note_attributes for server-side Conversions API tracking.
export async function createCheckout(lineItems, attributes = []) {
  try {
    console.log('Creating cart with items:', lineItems);

    const response = await fetch(getStorefrontApiUrl(), {
      method: 'POST',
      headers: getStorefrontHeaders(),
      body: JSON.stringify({
        query: CREATE_CART_MUTATION,
        variables: { lineItems, attributes }
      })
    });

    const data = await response.json();
    console.log('Cart creation response:', data);
    
    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      throw new Error(data.errors[0].message);
    }
    
    if (data.data?.cartCreate?.cart) {
      return {
        checkoutId: data.data.cartCreate.cart.id,
        checkoutUrl: data.data.cartCreate.cart.checkoutUrl
      };
    }
    
    if (data.data?.cartCreate?.userErrors?.length > 0) {
      console.error('User errors:', data.data.cartCreate.userErrors);
      throw new Error(data.data.cartCreate.userErrors[0].message);
    }
    
    throw new Error('Failed to create cart');
  } catch (error) {
    console.error('Error creating checkout:', error);
    throw error;
  }
}

// Update addToCheckout to use Cart API
export async function addToCheckout(cartId, lineItems) {
  try {
    const response = await fetch(getStorefrontApiUrl(), {
      method: 'POST',
      headers: getStorefrontHeaders(),
      body: JSON.stringify({
        query: ADD_TO_CART_MUTATION,
        variables: { cartId, lineItems }
      })
    });

    const data = await response.json();
    
    if (data.data?.cartLinesAdd?.cart) {
      return data.data.cartLinesAdd.cart;
    }
    
    if (data.data?.cartLinesAdd?.userErrors?.length > 0) {
      throw new Error(data.data.cartLinesAdd.userErrors[0].message);
    }
    
    throw new Error('Failed to add items to cart');
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
}

// Helper function to convert numeric ID to Shopify GID format
export function toShopifyGid(id, type = 'ProductVariant') {
  if (!id) return null;
  if (String(id).startsWith('gid://')) return id; // Already formatted
  return `gid://shopify/${type}/${id}`;
}

// Query to fetch Bundle Product with Selling Plans
const BUNDLE_PRODUCT_QUERY = `
  query getBundleProduct($handle: String!) {
    productByHandle(handle: $handle) {
      id
      handle
      title
      description
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      images(first: 5) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 10) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            image {
              url
              altText
            }
          }
        }
      }
      sellingPlanGroups(first: 5) {
        edges {
          node {
            name
            sellingPlans(first: 10) {
              edges {
                node {
                  id
                  name
                  description
                  priceAdjustments {
                    adjustmentValue {
                      ... on SellingPlanPercentagePriceAdjustment {
                        adjustmentPercentage
                      }
                      ... on SellingPlanFixedAmountPriceAdjustment {
                        adjustmentAmount {
                          amount
                          currencyCode
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Fetch Bundle Product from Shopify (complete-wellness-bundle)
export async function fetchBundleProduct() {
  const handle = PRODUCT_HANDLE_MAP['bundle'];
  
  console.log('🎁 Fetching bundle product from Shopify:', handle);
  
  try {
    const response = await fetch(getStorefrontApiUrl(), {
      method: 'POST',
      headers: getStorefrontHeaders(),
      body: JSON.stringify({
        query: BUNDLE_PRODUCT_QUERY,
        variables: { handle }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Bundle HTTP Error:', errorText);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('📦 Bundle API Response:', data);
    
    if (data.errors) {
      console.error('Bundle GraphQL errors:', data.errors);
      return null;
    }
    
    if (data.data?.productByHandle) {
      const product = data.data.productByHandle;
      
      // Extract main variant (first variant, usually the default)
      const mainVariant = product.variants.edges[0]?.node;
      
      // Extract selling plan (for subscription)
      const sellingPlan = product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges[0]?.node;
      
      // Extract images
      const productImages = product.images.edges.map(edge => edge.node.url);
      const mainImage = mainVariant?.image?.url || productImages[0] || '/products/bundle.png';
      
      console.log('🖼️ Bundle images found:', productImages.length, 'images');
      console.log('🖼️ Using main image:', mainImage);
      
      // Calculate subscription price (apply percentage discount)
      let subscriptionPrice = parseFloat(mainVariant.price.amount);
      if (sellingPlan?.priceAdjustments[0]?.adjustmentValue?.adjustmentPercentage) {
        const discountPercent = sellingPlan.priceAdjustments[0].adjustmentValue.adjustmentPercentage;
        subscriptionPrice = subscriptionPrice * (1 - discountPercent / 100);
      }
      
      const result = {
        id: 'bundle',
        shopifyProductId: product.id.split('/').pop(), // Extract numeric ID from GID
        shopifyVariantId: mainVariant.id.split('/').pop(), // Extract numeric ID
        shopifySellingPlanId: sellingPlan?.id.split('/').pop() || null,
        title: product.title,
        description: product.description,
        oneTimePrice: parseFloat(mainVariant.price.amount),
        subscriptionPrice: subscriptionPrice,
        currencyCode: mainVariant.price.currencyCode,
        available: mainVariant.availableForSale,
        variantGid: mainVariant.id,
        sellingPlanGid: sellingPlan?.id || null,
        image: mainImage, // Shopify product image
        images: productImages // All product images
      };
      
      console.log('✅ Bundle product fetched successfully:', result);
      return result;
    }
    
    console.log('❌ No bundle product found in Shopify');
    return null;
  } catch (error) {
    console.error('❌ Error fetching bundle product:', error);
    return null;
  }
}